import { defineStore } from "pinia"
import { ref, type Ref } from "vue"
import { Instruction } from "@/models/instruction.model"
import { useEventListener } from "@vueuse/core"
import { FileRelation } from "@/models/file/file-relation.model"
import FilePreviewTile from "@/components/file/FilePreviewTile.vue"

export const useDragStore = defineStore("dragStore", () => {
  const instructionBeingDragged: Ref<Instruction | null> = ref(null)

  const fileRelationBeingDragged: Ref<FileRelation | null> = ref(null)
  const fileRelationDragSource = ref<typeof FilePreviewTile | null>(null)

  function registerDrag(draggedFileRelation: FileRelation, component: typeof FilePreviewTile) {
    fileRelationBeingDragged.value = draggedFileRelation
    fileRelationDragSource.value = component
  }

  function unregisterDrag() {
    fileRelationBeingDragged.value = null
    fileRelationDragSource.value = null
  }

  function notifyMoveSuccesful() {
    // Notify the drag source that the move was successful
    fileRelationDragSource.value?.exposed.onMoveSuccessful()
  }

  const isDraggingGlobal = ref(false)
  const dragData: Ref<unknown> = ref(null)

  useEventListener(document, "dragover", (e) => {
    /*
     * Dragging instructions causes unexpected dragenter/dragleave events (possibly caused by draggable).
     * This leads to isDraggingGlobal not being reset to false when dropping an instruction outside the window.
     * Currently there is no need to support dropping instructions on drop zones, so we simply ignore the "move" effect.
     *
     * In the future we might consider using mouse enter event to reset isDraggingGlobal to false.
     */
    if (e.dataTransfer?.effectAllowed === "move") return

    e.preventDefault()
    isDraggingGlobal.value = true
  })

  useEventListener(document, "drop", (e) => {
    e.preventDefault()
    isDraggingGlobal.value = false
    dragEnterLeaveSum = 0 // Otherwise dragEnterLeaveSum will be 1 after drop
  })

  /*
  When user leaves the window while dragging, isDraggingGlobal should be set to false
  This requires us to keep track of the sum of dragenter and dragleave events
*/
  let dragEnterLeaveSum = 0
  useEventListener(document, "dragenter", (e) => {
    if (e.dataTransfer?.effectAllowed === "move") return // See dragover event

    e.preventDefault()
    dragEnterLeaveSum += 1
  })
  useEventListener(document, "dragleave", (e) => {
    if (e.dataTransfer?.effectAllowed === "move") return // See dragover event

    e.preventDefault()
    dragEnterLeaveSum -= 1

    if (dragEnterLeaveSum === 0) {
      isDraggingGlobal.value = false
    }
  })

  return {
    isDraggingGlobal,
    dragData,
    instructionBeingDragged,
    fileRelationBeingDragged,
    registerDrag,
    unregisterDrag,
    notifyMoveSuccesful
  }
})
